import { useState, useEffect } from 'react'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { useParams, Link } from 'react-router-dom'
import { useCampaignDashboard, useCampaignSubmissions } from '@/hooks/useCampaigns'
import { ArrowLeft, Download, ExternalLink, ChevronLeft, ChevronRight, Search, Eye, X } from 'lucide-react'
import api from '@/lib/api'

const MATURITY_COLORS: Record<string, string> = {
  avancado: 'bg-emerald-500',
  intermediario: 'bg-amber-500',
  inicial: 'bg-red-500',
}

const MATURITY_LABELS: Record<string, string> = {
  avancado: 'Avançado',
  intermediario: 'Intermediário',
  inicial: 'Inicial',
}

const FIELD_LABELS: Record<string, string> = {
  razao_social: 'Razão Social', nome_fantasia: 'Nome Fantasia',
  cnpj: 'CNPJ', setor: 'Setor de Atuação', porte: 'Porte',
  faturamento: 'Faixa de Faturamento', colaboradores: 'Nº Colaboradores',
  regime: 'Regime Tributário', contato_nome: 'Nome do Contato',
  contato_email: 'E-mail', contato_telefone: 'Telefone', contato_cargo: 'Cargo',
  missao: 'Missão definida', visao: 'Visão definida', valores: 'Valores definidos',
  metas: 'Metas estratégicas', processos: 'Processos mapeados',
  organograma: 'Organograma atualizado',
  cod_conduta: 'Código de Conduta', canal_denuncia: 'Canal de Denúncia',
  aud_freq: 'Auditoria frequente', stake: 'Engajamento Stakeholders',
  metas_esg: 'Metas ESG definidas', gov_esg: 'Comitê/Governança ESG',
  clima: 'Pesquisa de Clima', treinos: 'Programa de Treinamento',
  sst: 'Saúde e Segurança', carreira: 'Plano de Carreira',
  di: 'Diversidade & Inclusão', bemestar: 'Programa de Bem-estar',
  efluentes: 'Gestão de Efluentes', quimicos: 'Gestão de Químicos',
  lic: 'Licenciamento Ambiental', gee: 'Inventário GEE',
  clima_risco: 'Análise de Riscos Climáticos',
}

function FormDataModal({ submissionId, onClose }: { submissionId: string; onClose: () => void }) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get(`/v1/diagnostics/${submissionId}/form-data`)
      .then(res => setData(res.data))
      .catch(() => setError('Erro ao carregar dados'))
      .finally(() => setLoading(false))
  }, [submissionId])

  const getValueColor = (val: string) => {
    if (val === 'Sim') return 'text-emerald-500 font-semibold'
    if (val === 'Parcial') return 'text-amber-500 font-semibold'
    if (val === 'Não' || val === 'Nao') return 'text-red-500 font-semibold'
    return ''
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-10 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-10">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold">Dados do Diagnóstico</h2>
            {data && <p className="text-sm text-muted-foreground">{data.company_name}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>
          ) : error ? (
            <p className="text-center text-red-500 py-8">{error}</p>
          ) : data?.form_data ? (
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(data.form_data).filter(([k, v]) => !k.startsWith('_') && v).map(([key, value]) => (
                  <tr key={key} className="border-b last:border-0">
                    <td className="py-2 px-3 text-muted-foreground font-medium w-1/3">
                      {FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </td>
                    <td className={`py-2 px-3 ${getValueColor(String(value))}`}>
                      {String(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center text-muted-foreground py-8">Sem dados disponíveis.</p>
          )}
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <a
            href={`/api/v1/diagnostics/${submissionId}/pdf?include_data=true`}
            target="_blank"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" /> PDF com Dados
          </a>
          <button onClick={onClose} className="px-4 py-1.5 text-sm font-medium bg-accent hover:bg-accent/80 rounded-lg transition-colors">
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

export default function CampaignDashboardPage() {
  const { id } = useParams<{ id: string }>()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [maturityFilter, setMaturityFilter] = useState('')
  const [viewingSubmission, setViewingSubmission] = useState<string | null>(null)

  const { data: dashboard, isLoading: dashLoading } = useCampaignDashboard(id || '')
  const { data: submissions, isLoading: subLoading } = useCampaignSubmissions(id || '', { page, size: 15, search: search || undefined, maturity: maturityFilter || undefined })

  if (dashLoading) {
    return <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>
  }

  if (!dashboard) {
    return <div className="p-6"><p className="text-muted-foreground">Campanha não encontrada.</p></div>
  }

  const totalMat = Object.values(dashboard.maturity_distribution).reduce((a, b) => a + b, 0) || 1

  return (
    <div className="p-6 space-y-6">
      {viewingSubmission && <FormDataModal submissionId={viewingSubmission} onClose={() => setViewingSubmission(null)} />}

      <div className="flex items-center gap-4">
        <Link to="/campaigns" className="p-2 hover:bg-accent rounded-lg"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{dashboard.campaign.name}</h1>
          <p className="text-sm text-muted-foreground">{dashboard.campaign.description}</p>
        </div>
        <a
          href="/diagnostico-esg/"
          target="_blank"
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <ExternalLink className="w-4 h-4" /> Landing Page
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold">{dashboard.total_submissions}</p>
          <p className="text-xs text-muted-foreground mt-1">Submissões</p>
        </div>
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold">{dashboard.lead_conversion_count}</p>
          <p className="text-xs text-muted-foreground mt-1">Leads Criados</p>
        </div>
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-emerald-500">{dashboard.avg_score_overall?.toFixed(0) || '—'}%</p>
          <p className="text-xs text-muted-foreground mt-1">Score Geral</p>
        </div>
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-blue-500">{dashboard.avg_score_strategy?.toFixed(0) || '—'}%</p>
          <p className="text-xs text-muted-foreground mt-1">Estratégia</p>
        </div>
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-purple-500">{dashboard.avg_score_governance?.toFixed(0) || '—'}%</p>
          <p className="text-xs text-muted-foreground mt-1">Governança</p>
        </div>
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-amber-500">{dashboard.avg_score_social?.toFixed(0) || '—'}%</p>
          <p className="text-xs text-muted-foreground mt-1">Social</p>
        </div>
      </div>

      <div className="bg-card border rounded-lg p-5">
        <h3 className="font-semibold mb-3">Distribuição de Maturidade</h3>
        <div className="flex h-8 rounded-lg overflow-hidden">
          {Object.entries(dashboard.maturity_distribution).map(([level, count]) => (
            <div
              key={level}
              className={`${MATURITY_COLORS[level] || 'bg-gray-500'} flex items-center justify-center text-xs font-bold text-white`}
              style={{ width: `${(count / totalMat) * 100}%` }}
              title={`${MATURITY_LABELS[level]}: ${count}`}
            >
              {count > 0 && MATURITY_LABELS[level]}
            </div>
          ))}
        </div>
        <div className="flex gap-4 mt-2">
          {Object.entries(dashboard.maturity_distribution).map(([level, count]) => (
            <span key={level} className="text-xs text-muted-foreground">
              <span className={`inline-block w-2 h-2 rounded-full ${MATURITY_COLORS[level]} mr-1`} />
              {MATURITY_LABELS[level]}: {count} ({((count / totalMat) * 100).toFixed(0)}%)
            </span>
          ))}
        </div>
      </div>

      {Object.keys(dashboard.sector_distribution).length > 0 && (
        <div className="bg-card border rounded-lg p-5">
          <h3 className="font-semibold mb-3">Setores</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(dashboard.sector_distribution)
              .sort((a, b) => b[1] - a[1])
              .map(([sector, count]) => (
                <span key={sector} className="inline-flex items-center gap-1 px-3 py-1 bg-accent rounded-full text-sm">
                  {sector} <span className="font-bold">{count}</span>
                </span>
              ))}
          </div>
        </div>
      )}

      <div className="bg-card border rounded-lg">
        <div className="p-4 border-b flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <h3 className="font-semibold">Submissões</h3>
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar empresa ou contato..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                className="pl-9 pr-3 py-1.5 bg-background border rounded-lg text-sm w-64"
              />
            </div>
            <select
              value={maturityFilter}
              onChange={(e) => { setMaturityFilter(e.target.value); setPage(1) }}
              className="px-3 py-1.5 bg-background border rounded-lg text-sm"
            >
              <option value="">Todos</option>
              <option value="avancado">Avançado</option>
              <option value="intermediario">Intermediário</option>
              <option value="inicial">Inicial</option>
            </select>
          </div>
        </div>

        {subLoading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>
        ) : !submissions || submissions.items.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">Nenhuma submissão encontrada.</div>
        ) : (
          <>
            <DataGrid
              gridId="campaign-submissions"
              columns={[
                { id: 'company', header: 'Empresa', accessor: (sub: any) => <div><div className="font-medium">{sub.company_name}</div>{sub.employee_count && <div className="text-xs text-muted-foreground">{sub.employee_count} colaboradores</div>}</div>, sortValue: (s: any) => s.company_name, filterValue: (s: any) => s.company_name },
                { id: 'contact', header: 'Contato', accessor: (sub: any) => <div><div>{sub.contact_name}</div><div className="text-xs text-muted-foreground">{sub.contact_email}</div></div>, sortValue: (s: any) => s.contact_name, filterValue: (s: any) => s.contact_name + ' ' + s.contact_email },
                { id: 'sector', header: 'Setor', accessor: (sub: any) => <span className="text-muted-foreground">{sub.sector || '—'}</span>, sortValue: (s: any) => s.sector || '', filterValue: (s: any) => s.sector || '' },
                { id: 'score', header: 'Score', accessor: (sub: any) => <span className="font-bold">{sub.score_overall !== null ? sub.score_overall.toFixed(0) + '%' : '—'}</span>, sortValue: (s: any) => s.score_overall ?? 0, filterValue: (s: any) => s.score_overall !== null ? s.score_overall.toFixed(0) + '%' : '', align: 'center' as const },
                { id: 'maturity', header: 'Maturidade', accessor: (sub: any) => sub.maturity_level ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sub.maturity_level === 'avancado' ? 'bg-emerald-500/10 text-emerald-500' : sub.maturity_level === 'intermediario' ? 'bg-amber-500/10 text-amber-500' : 'bg-red-500/10 text-red-500'}`}>{MATURITY_LABELS[sub.maturity_level] || sub.maturity_level}</span> : null, sortValue: (s: any) => s.maturity_level || '', filterValue: (s: any) => MATURITY_LABELS[s.maturity_level] || s.maturity_level || '', align: 'center' as const },
                { id: 'date', header: 'Data', accessor: (sub: any) => <span className="text-muted-foreground text-sm">{new Date(sub.created_at).toLocaleDateString('pt-BR')}</span>, sortValue: (s: any) => new Date(s.created_at).getTime(), filterValue: (s: any) => new Date(s.created_at).toLocaleDateString('pt-BR') },
                { id: 'actions', header: 'Ações', sortable: false, filterable: false, align: 'center' as const, accessor: (sub: any) => <div className="flex items-center justify-center gap-1"><button onClick={() => setViewingSubmission(sub.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-500 hover:bg-blue-500/10 rounded transition-colors" title="Ver dados"><Eye className="w-3 h-3" /> Dados</button><a href={'/api/v1/diagnostics/' + sub.id + '/pdf'} target="_blank" className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-500 hover:bg-emerald-500/10 rounded transition-colors" title="PDF"><Download className="w-3 h-3" /> PDF</a></div> },
              ]}
              data={submissions?.items || []}
              isLoading={subLoading}
              emptyMessage="Nenhuma submissão encontrada."
              pageSize={15}
            />
          </>
        )}
      </div>
    </div>
  )
}
